import { bioThemes, themeKeys } from './bioThemes'

export interface AppearanceData {
  theme: string
  background_type: string
  background_value: string | null
  button_style: string
  button_color: string
  text_color: string
  font_family: string
}

interface Props {
  data: AppearanceData
  onChange: (updates: Partial<AppearanceData>) => void
}

const fonts = ['Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins']

export function BioAppearanceEditor({ data, onChange }: Props) {
  return (
    <div className="max-w-3xl mx-auto space-y-8 pb-8">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-white/10 pb-4">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Appearance</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Customize the look and feel of your bio page</p>
      </div>

      {/* Theme Picker */}
      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Themes</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {themeKeys.map(key => {
            const theme = bioThemes[key]
            const selected = data.theme === key
            const bg = theme.background
            return (
              <button
                key={key}
                onClick={() => onChange({ theme: key })}
                className={`group relative flex flex-col items-center text-left transition-all ${selected ? 'ring-2 ring-[#2B6CEE] ring-offset-2 dark:ring-offset-[#0d121b]' : 'hover:opacity-90'
                  } rounded-xl`}
              >
                <div
                  className="w-full aspect-[3/4] rounded-xl shadow-sm border border-gray-200 dark:border-white/10 mb-2 overflow-hidden relative"
                  style={{ background: bg.startsWith('linear') ? bg : bg }}
                >
                  {/* Mini Preview of Theme */}
                  <div className="absolute inset-0 p-3 flex flex-col items-center gap-2 pt-6">
                    <div className="w-8 h-8 rounded-full bg-white/20 mb-1" />
                    <div className="w-16 h-2 rounded-full bg-white/20" />
                    <div className="w-full mt-2 space-y-1.5 px-2">
                      <div className="w-full h-6 rounded-md" style={{
                        background: theme.buttonBg === 'transparent' ? 'transparent' : theme.buttonBg,
                        border: theme.buttonBg === 'transparent' ? `1px solid ${theme.buttonBorder || theme.buttonText}` : 'none'
                      }} />
                      <div className="w-full h-6 rounded-md" style={{
                        background: theme.buttonBg === 'transparent' ? 'transparent' : theme.buttonBg,
                        border: theme.buttonBg === 'transparent' ? `1px solid ${theme.buttonBorder || theme.buttonText}` : 'none',
                        opacity: 0.7
                      }} />
                    </div>
                  </div>

                  {selected && (
                    <div className="absolute inset-0 bg-[#2B6CEE]/10 flex items-center justify-center backdrop-blur-[1px]">
                      <div className="w-8 h-8 bg-[#2B6CEE] rounded-full flex items-center justify-center shadow-lg transform scale-100 transition-transform">
                        <span className="material-symbols-outlined text-white text-[18px]">check</span>
                      </div>
                    </div>
                  )}
                </div>
                <span className={`text-sm font-medium ${selected ? 'text-[#2B6CEE]' : 'text-gray-700 dark:text-gray-300'}`}>
                  {theme.name}
                </span>
              </button>
            )
          })}
        </div>
      </section>

      {/* Background Settings */}
      <section className="pt-6 border-t border-gray-200 dark:border-white/10">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Background</h3>
        <div className="space-y-4">
          <div className="flex p-1 bg-gray-100 dark:bg-white/5 rounded-lg w-fit">
            {['color', 'gradient', 'image'].map(type => (
              <button
                key={type}
                onClick={() => onChange({ background_type: type })}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${data.background_type === type
                    ? 'bg-white dark:bg-[#1c2230] text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>

          <div className="p-4 border border-gray-200 dark:border-white/10 rounded-xl bg-gray-50 dark:bg-transparent">
            {data.background_type === 'color' && (
              <div className="flex items-center gap-4">
                <div className="relative">
                  <input
                    type="color"
                    value={data.background_value || '#ffffff'}
                    onChange={e => onChange({ background_value: e.target.value })}
                    className="w-12 h-12 rounded-lg border-2 border-white dark:border-gray-600 shadow-sm cursor-pointer p-0 overflow-hidden"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Color Value</label>
                  <input
                    type="text"
                    value={data.background_value || ''}
                    onChange={e => onChange({ background_value: e.target.value })}
                    placeholder="#ffffff"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2B6CEE]"
                  />
                </div>
              </div>
            )}

            {data.background_type === 'gradient' && (
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">CSS Gradient</label>
                <input
                  type="text"
                  value={data.background_value || ''}
                  onChange={e => onChange({ background_value: e.target.value })}
                  placeholder="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2B6CEE]"
                />
                <p className="text-xs text-gray-400 mt-2">
                  Tip: Copy gradients from <a href="https://uigradients.com" target="_blank" rel="noreferrer" className="text-[#2B6CEE] hover:underline">uiGradients</a>
                </p>
              </div>
            )}

            {data.background_type === 'image' && (
              <div className="space-y-3">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Image URL</label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={data.background_value || ''}
                    onChange={e => onChange({ background_value: e.target.value })}
                    placeholder="https://example.com/background.jpg"
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2B6CEE]"
                  />
                  <button className="px-4 py-2 text-sm font-medium text-white bg-[#2B6CEE] rounded-lg hover:bg-[#2454c7] transition-colors">
                    Upload
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Buttons & Typography */}
      <section className="pt-6 border-t border-gray-200 dark:border-white/10">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Buttons & Typography</h3>
        <div className="space-y-6">
          {/* Button Style */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Button Style</label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'rounded', label: 'Rounded', radius: '12px' },
                { value: 'square', label: 'Square', radius: '4px' },
                { value: 'pill', label: 'Pill', radius: '50px' },
              ].map(style => (
                <button
                  key={style.value}
                  onClick={() => onChange({ button_style: style.value })}
                  className={`py-3 text-sm font-medium border transition-all hover:bg-gray-50 dark:hover:bg-white/5 ${data.button_style === style.value
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-[#2B6CEE] border-[#2B6CEE]'
                      : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'
                    }`}
                  style={{ borderRadius: style.radius }}
                >
                  {style.label}
                </button>
              ))}
            </div>
          </div>

          {/* Colors */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Button Color</label>
              <div className="flex items-center gap-3 p-3 border border-gray-200 dark:border-white/10 rounded-lg">
                <input
                  type="color"
                  value={data.button_color || '#4F46E5'}
                  onChange={e => onChange({ button_color: e.target.value })}
                  className="w-10 h-10 rounded border border-gray-200 cursor-pointer"
                />
                <input
                  type="text"
                  value={data.button_color || ''}
                  onChange={e => onChange({ button_color: e.target.value })}
                  className="flex-1 px-2 py-1.5 text-sm border-none bg-transparent text-gray-900 dark:text-white focus:outline-none"
                  placeholder="#000000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Text Color</label>
              <div className="flex items-center gap-3 p-3 border border-gray-200 dark:border-white/10 rounded-lg">
                <input
                  type="color"
                  value={data.text_color || '#1F2937'}
                  onChange={e => onChange({ text_color: e.target.value })}
                  className="w-10 h-10 rounded border border-gray-200 cursor-pointer"
                />
                <input
                  type="text"
                  value={data.text_color || ''}
                  onChange={e => onChange({ text_color: e.target.value })}
                  className="flex-1 px-2 py-1.5 text-sm border-none bg-transparent text-gray-900 dark:text-white focus:outline-none"
                  placeholder="#000000"
                />
              </div>
            </div>
          </div>

          {/* Font Family */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Font Family</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {fonts.map(font => (
                <button
                  key={font}
                  onClick={() => onChange({ font_family: font })}
                  className={`py-3 px-2 text-sm border rounded-lg transition-all ${data.font_family === font
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-[#2B6CEE] border-[#2B6CEE]'
                      : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                    }`}
                  style={{ fontFamily: font }}
                >
                  {font}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
